<template>
  <div class="pb-1">
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="6">
          <e-store-combo
            id="cost-contract-supplier-filter-store"
            v-model="filters.storeId"
            :required="false"
            :only-active-options="true"
          />
        </b-col>
        <b-col md="6">
          <e-search-sku
            id="cost-contract-supplier-filter-sku"
            v-model="filters.skuId"
            :required="false"
            :only-active="true"
            :include-fixed-assets="true"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <b-table
        ref="cost-contract-supplier-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('COST_CONTRACT_SUPPLIER.NAME'), 'male', queryExecuted)"
        :fields="fields"
        :items="sortedContracts"
      >
        <template #cell(storeId)="row">
          <b-badge
            v-if="!row.item.storeId"
            variant="light-secondary"
          >
            {{ $t('Geral') }}
          </b-badge>
          <span v-else>{{ getStoreName(row.item.storeId) }}</span>
        </template>

        <template #cell(skuId)="row">
          {{ row.item.skuId }}{{ row.item.skuName ? ' - ' + row.item.skuName : '' }}
        </template>

        <template #cell(costValue)="row">
          {{ row.item.costValue | currency }}
        </template>

        <template #cell(validFrom)="row">
          {{ row.item.validFrom | datetime }}
        </template>

        <template #cell(validTo)="row">
          <span v-if="row.item.validTo">{{ row.item.validTo | datetime }}</span>
          <b-badge
            v-else
            variant="light-secondary"
          >
            {{ $t('sem limite') }}
          </b-badge>
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :show-update="$can('Update', 'SkuCostContractSupplier')"
            :show-delete="$can('Delete', 'SkuCostContractSupplier')"
            @update="showUpdateSidebar(row)"
            @delete="deleteContractLocal(row)"
          />
        </template>
      </b-table>
    </b-card-actions>

    <cost-contract-supplier-sidebar
      ref="cost_contract_supplier_sidebar"
      @save="getData"
    />

    <fab
      v-if="$can('Create', 'SkuCostContractSupplier')"
      :main-tooltip="$t('Adicionar Custo Contratado')"
      @click="showCreateSidebar"
    />
  </div>
</template>

<script>
import { BRow, BCol, BTable, BBadge } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { EFilters, EGridActions } from '@/views/components'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import ESearchSku from '@/views/components/inputs/ESearchSku.vue'
import fab from '@/views/components/FAB.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import CostContractSupplierSidebar from './CostContractSupplierSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BBadge,
    BCardActions,
    EFilters,
    EGridActions,
    EStoreCombo,
    ESearchSku,
    fab,
    CostContractSupplierSidebar,
  },

  data() {
    return {
      queryExecuted: false,
      fetching: false,
    }
  },

  computed: {
    ...mapState('pages/catalog/costContractSuppliers', {
      contracts: 'contracts',
      filters: 'filters',
    }),
    ...mapGetters('app', ['allStoreOptions']),

    // Reforçamos a ordem exibida por Loja, depois SKU e depois Vigência de no client.
    sortedContracts() {
      return [...(this.contracts || [])].sort((a, b) => {
        if ((a.storeId || 0) !== (b.storeId || 0)) return (a.storeId || 0) - (b.storeId || 0)
        if (a.skuId !== b.skuId) return a.skuId - b.skuId
        return new Date(a.validFrom) - new Date(b.validFrom)
      })
    },

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Id'),
          key: 'id',
          class: 'text-center',
          thStyle: { width: '80px' },
        },
        {
          label: this.$t('Loja'),
          key: 'storeId',
          thClass: 'text-center',
          tdClass: 'text-left',
          thStyle: { width: '260px' },
        },
        {
          label: this.$t('Produto'),
          key: 'skuId',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Custo'),
          key: 'costValue',
          thClass: 'text-center',
          tdClass: 'text-right',
          thStyle: { width: '140px' },
        },
        {
          label: this.$t('Vigência de'),
          key: 'validFrom',
          class: 'text-center',
          thStyle: { width: '160px' },
        },
        {
          label: this.$t('Vigência até'),
          key: 'validTo',
          class: 'text-center',
          thStyle: { width: '160px' },
        },
      ]
    },
  },

  methods: {
    ...mapActions('pages/catalog/costContractSuppliers', [
      'fetchContracts',
      'deleteContract',
      'resetFilters',
    ]),

    getStoreName(storeId) {
      const store = this.allStoreOptions?.find(s => s.value === storeId)
      return store ? store.label : storeId
    },

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchContracts()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    filterData() {
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    showCreateSidebar() {
      this.$refs.cost_contract_supplier_sidebar.show()
    },

    showUpdateSidebar(row) {
      this.$refs.cost_contract_supplier_sidebar.show({ item: row.item })
    },

    async deleteContractLocal(row) {
      const { id } = row.item
      try {
        const confirm = await this.confirm()
        if (confirm) {
          await this.deleteContract(id)
          this.getData()
          this.showSuccess({
            message: this.$t('Custo contratado removido com sucesso'),
          })
        }
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style></style>
